import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { STATS, VALUES } from "@/lib/data";

export default function About() {
  return (
    <div data-testid="about-page" className="bg-bone">
      {/* HERO */}
      <section className="pt-20 md:pt-28 pb-20 md:pb-28 border-b border-border">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="overline text-vermilion mb-8">About ThinkRight</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              A Global Capability Center
              <br />
              <span className="italic text-ink/70">built like a product studio.</span>
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4 lg:pl-10 lg:border-l border-border">
            <p className="text-lg text-ink/70 leading-relaxed">
              We are a standing team of senior engineers, ML researchers and product
              designers, embedded with a small portfolio of venture-backed product
              companies. We ship the software those companies take to market.
            </p>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 md:py-32">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-vermilion">Our thesis</div>
          </div>
          <Reveal className="col-span-12 md:col-span-8">
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ink">
              The conventional GCC was built for <span className="italic">cost arbitrage</span>.
              We were built for the opposite: a single, senior, embedded team that
              behaves like a second product org — so founders can move at venture
              pace without assembling a company behind a company.
            </p>
            <p className="mt-10 text-lg text-ink/70 max-w-3xl leading-relaxed">
              This is why companies like Matchbook.ai trust us with
              the products their customers depend on. We don’t take
              tickets — we take outcomes. We don’t augment staff — we architect,
              build and operate.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 border-y border-border">
        <div className="tr-container grid grid-cols-2 md:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal
              key={s.label}
              delay={i}
              className={`p-8 ${i !== 3 ? "md:border-r border-border" : ""} ${
                i < 2 ? "border-b md:border-b-0 border-border" : ""
              }`}
            >
              <div className="display-num text-5xl md:text-6xl text-ink">{s.value}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.18em] text-ink/50 max-w-[26ch]">
                {s.label}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32">
        <div className="tr-container">
          <div className="grid grid-cols-12 gap-10 mb-16">
            <div className="col-span-12 md:col-span-5">
              <div className="overline text-vermilion mb-6">What we value</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
                Four principles.
                <br />
                <span className="italic text-ink/60">Repeated, out loud.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 border-t border-border">
            {VALUES.map((v, i) => (
              <Reveal
                key={v.n}
                delay={i}
                className={`col-span-12 md:col-span-6 p-10 border-b border-border ${
                  i % 2 === 0 ? "md:border-r" : ""
                }`}
              >
                <div className="flex items-baseline gap-6 mb-4">
                  <span className="font-mono text-xs text-vermilion">{v.n}</span>
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
                    {v.title}
                  </h3>
                </div>
                <p className="text-base text-ink/70 leading-relaxed max-w-prose pl-[52px]">
                  {v.body}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="py-24 md:py-32 bg-ink text-white">
        <div className="tr-container grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-vermilion mb-6">How we engage</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Three engagement models.
              <br />
              <span className="italic text-white/60">All of them embedded.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7">
            {[
              {
                n: "01",
                title: "Founding GCC",
                body: "We become your engineering backbone from seed to Series B — product, platform, AI — under one accountable leadership team.",
              },
              {
                n: "02",
                title: "AI pod",
                body: "A senior applied-AI pod drops in, codifies evals, ships your first 2–3 AI features, and transfers the playbook to your in-house team.",
              },
              {
                n: "03",
                title: "Research engagement",
                body: "A 6-to-12-week research sprint on a single high-risk question — prototypes, benchmarks, and a founder-ready recommendation.",
              },
            ].map((m, i) => (
              <Reveal
                key={m.n}
                delay={i}
                className="py-8 border-b border-white/15 grid grid-cols-12 gap-6"
              >
                <span className="font-mono text-xs text-white/50 col-span-2 md:col-span-1 pt-1">
                  {m.n}
                </span>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight col-span-10 md:col-span-4">
                  {m.title}
                </h3>
                <p className="text-white/70 leading-relaxed col-span-12 md:col-span-7">
                  {m.body}
                </p>
              </Reveal>
            ))}
            <Link
              to="/contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-2 mt-12 border border-white px-7 py-4 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-white hover:text-ink transition-colors"
            >
              Start a conversation <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
